import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pl';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useGetCashBalanceQuery } from '../../Service/extendedCashDocumentsApi';
import { formatAmount } from '../../Types/cashDocumentTypes';
import type { CashBalanceWidgetProps } from './CashBalanceWidget.types';
import { getCashDocumentsErrorMessage } from '../../Utils/cashDocumentsErrorMapping';

const CashBalanceWidget: React.FC<CashBalanceWidgetProps> = ({
  defaultCurrency = 'PLN',
  showCurrencySelector = true,
  defaultRangeDays = 30,
}) => {
  const [currency, setCurrency] = useState(defaultCurrency);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().subtract(defaultRangeDays, 'day'));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());

  // Format dates for API (yyyy-MM-dd)
  const fromDate = useMemo(() => dateFrom?.format('YYYY-MM-DD') || '', [dateFrom]);
  const toDate = useMemo(() => dateTo?.format('YYYY-MM-DD') || '', [dateTo]);

  // Fetch balance data
  const {
    data: balance,
    isLoading,
    error,
  } = useGetCashBalanceQuery(
    {
      from: fromDate,
      to: toDate,
      currency,
    },
    {
      skip: !fromDate || !toDate,
    }
  );

  // Console debugging
  console.log('[CashBalanceWidget] Query Params:', { from: fromDate, to: toDate, currency });
  console.log('[CashBalanceWidget] Balance Data:', balance);
  console.log('[CashBalanceWidget] IsLoading:', isLoading);
  console.log('[CashBalanceWidget] Error:', error);

  // Calculate net balance
  const netBalance = useMemo(() => {
    if (!balance) return 0;
    return balance.totalKP - balance.totalKW;
  }, [balance]);

  // Determine color based on balance
  const getBalanceColor = (amount: number): string => {
    if (amount > 0) return 'success.main';
    if (amount < 0) return 'error.main';
    return 'text.secondary';
  };

  // Render balance icon
  const getBalanceIcon = (amount: number) => {
    if (amount > 0) return <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />;
    if (amount < 0) return <TrendingDownIcon sx={{ fontSize: 40, color: 'error.main' }} />;
    return <AccountBalanceWalletIcon sx={{ fontSize: 40, color: 'text.secondary' }} />;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
      <Card elevation={2}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AccountBalanceWalletIcon />
            Stan kasy
          </Typography>

          {/* Date Range & Currency Filters */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }} >
                <DatePicker
                  label="Data od"
                  value={dateFrom}
                  onChange={(newValue) => setDateFrom(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} >
                <DatePicker
                  label="Data do"
                  value={dateTo}
                  onChange={(newValue) => setDateTo(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                    },
                  }}
                />
              </Grid>
              {showCurrencySelector && (
                <Grid size={{ xs: 12, sm: 4 }} >
                  <FormControl fullWidth size="small">
                    <InputLabel>Waluta</InputLabel>
                    <Select
                      value={currency}
                      label="Waluta"
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      <MenuItem value="PLN">PLN</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="GBP">GBP</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getCashDocumentsErrorMessage(
                error,
                'Błąd pobierania stanu kasy. Spróbuj ponownie.'
              )}
            </Alert>
          )}

          {/* Balance Summary */}
          {balance && !isLoading && (
            <Stack spacing={2}>
              {/* Net Balance (Main Display) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getBalanceIcon(netBalance)}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Saldo netto
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 'bold',
                        color: getBalanceColor(netBalance),
                      }}
                    >
                      {formatAmount(netBalance, currency)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* KP / KW Breakdown */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }} >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.light',
                      bgcolor: 'success.lighter',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Suma KP (przychody)
                    </Typography>
                    <Typography variant="h6" color="success.main" fontWeight="medium">
                      {formatAmount(balance.totalKP, currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {balance.kpCount} dokument{balance.kpCount !== 1 ? 'ów' : ''}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }} >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'error.light',
                      bgcolor: 'error.lighter',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Suma KW (wydatki)
                    </Typography>
                    <Typography variant="h6" color="error.main" fontWeight="medium">
                      {formatAmount(balance.totalKW, currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {balance.kwCount} dokument{balance.kwCount !== 1 ? 'ów' : ''}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Date Range Info */}
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Okres: {dateFrom?.format('DD.MM.YYYY')} - {dateTo?.format('DD.MM.YYYY')}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
};

export default CashBalanceWidget;
